import { useState, useEffect, useCallback } from 'react';
import { Play, Loader2, CheckCircle, AlertTriangle, X, Eye } from 'lucide-react';
import { onSocketReady, getSocket } from '@/lib/socket';
import type { RepairActionResult } from '../../invoice-repair/types';

type CardState = 'idle' | 'previewing' | 'previewed' | 'executing' | 'done' | 'error';

interface RepairActionCardProps {
  title: string;
  description: string;
  count: number;
  severity: 'error' | 'warning' | 'info';
  destructive?: boolean;
  onPreview: () => Promise<RepairActionResult>;
  onExecute: () => Promise<RepairActionResult>;
  disabled?: boolean;
  onComplete?: (result: RepairActionResult) => void;
}

export function RepairActionCard({
  title,
  description,
  count,
  severity,
  destructive = false,
  onPreview,
  onExecute,
  disabled = false,
  onComplete,
}: RepairActionCardProps) {
  const [state, setState] = useState<CardState>('idle');
  const [preview, setPreview] = useState<RepairActionResult | null>(null);
  const [result, setResult] = useState<RepairActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; current: number; total: number } | null>(null);

  // Close confirm dialog on Escape
  useEffect(() => {
    if (!showConfirm) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowConfirm(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showConfirm]);

  // Listen for WebSocket progress during execution
  const handleProgress = useCallback((data: { action: string; phase: string; current: number; total: number }) => {
    setProgress({ phase: data.phase, current: data.current, total: data.total });
  }, []);

  useEffect(() => {
    if (state !== 'executing') {
      setProgress(null);
      return;
    }
    const cleanup = onSocketReady((socket) => {
      socket.on('repair:progress', handleProgress);
    });
    return () => {
      cleanup?.();
      getSocket()?.off('repair:progress', handleProgress);
    };
  }, [state, handleProgress]);

  if (count === 0) return null;

  const severityColors = {
    error: 'border-danger/20 bg-danger/5',
    warning: 'border-warning/20 bg-yellow-500/5',
    info: 'border-accent-primary/20 bg-accent-primary/5',
  };

  const severityIcon = {
    error: <AlertTriangle className="w-4 h-4 text-danger" />,
    warning: <AlertTriangle className="w-4 h-4 text-warning" />,
    info: <Eye className="w-4 h-4 text-accent-primary" />,
  };

  const handlePreview = async () => {
    setState('previewing');
    setError(null);
    try {
      const res = await onPreview();
      setPreview(res);
      setState('previewed');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Błąd podglądu');
      setState('error');
    }
  };

  const handleExecute = async () => {
    if (destructive && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    if (destructive && confirmText !== 'POTWIERDŹ') return;

    setShowConfirm(false);
    setConfirmText('');
    setState('executing');
    setError(null);
    try {
      const res = await onExecute();
      setResult(res);
      setState('done');
      onComplete?.(res);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Błąd wykonania');
      setState('error');
    }
  };

  return (
    <div className={`rounded-xl border p-4 ${severityColors[severity]}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5">{severityIcon[severity]}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-text-primary">{title}</h4>
            <span className="text-xs px-2 py-0.5 rounded-full bg-surface-hover/60 text-text-secondary">{count}</span>
          </div>
          <p className="text-xs text-text-secondary mt-0.5">{description}</p>

          {/* Preview button */}
          {state === 'idle' && (
            <button
              onClick={handlePreview}
              disabled={disabled}
              className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-secondary hover:border-accent-primary/30 hover:text-accent-primary transition-all disabled:opacity-50"
            >
              <Eye className="w-3.5 h-3.5" />
              Podgląd
            </button>
          )}

          {/* Loading */}
          {state === 'previewing' && (
            <div className="mt-3 flex items-center gap-2 text-xs text-text-secondary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Generowanie podglądu...
            </div>
          )}

          {/* Executing with progress */}
          {state === 'executing' && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {progress ? progress.phase : 'Wykonywanie...'}
                {progress && progress.total > 0 && (
                  <span className="text-text-muted">({progress.current}/{progress.total})</span>
                )}
              </div>
              {progress && progress.total > 0 && (
                <div className="w-full h-1.5 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent-primary rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Preview results */}
          {state === 'previewed' && preview && (
            <div className="mt-3 space-y-2">
              <div className="p-3 rounded-lg bg-surface-raised/80 border border-border-subtle max-h-60 overflow-y-auto">
                <p className="text-xs text-text-secondary mb-2">{preview.message}</p>
                {preview.invoices && preview.invoices.length > 0 && (
                  <div className="space-y-1">
                    {preview.invoices.slice(0, 50).map((inv: any, i: number) => (
                      <div key={i} className="text-[11px] text-text-secondary flex gap-2">
                        <span className="text-text-muted">{i + 1}.</span>
                        <span>{inv.orderNumber || inv.number || inv.invoiceNumber || `ID: ${inv.id}`}</span>
                        {inv.oldNumber && inv.newNumber && (
                          <span className="text-text-muted">{inv.oldNumber} → <span className="text-accent-primary">{inv.newNumber}</span></span>
                        )}
                      </div>
                    ))}
                    {preview.invoices.length > 50 && (
                      <p className="text-[11px] text-text-muted mt-1">...i {preview.invoices.length - 50} więcej</p>
                    )}
                  </div>
                )}
                {preview.results && preview.results.length > 0 && (
                  <div className="space-y-1">
                    {preview.results.slice(0, 50).map((r: any, i: number) => (
                      <div key={i} className="text-[11px] text-text-secondary">
                        {r.orderNumber || r.invoiceNumber || r.number || JSON.stringify(r).slice(0, 80)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {(preview.count ?? count) > 0 ? (
                  <button
                    onClick={handleExecute}
                    disabled={disabled}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-primary/10 border border-accent-primary/30 text-xs text-accent-primary hover:bg-accent-primary/20 transition-all disabled:opacity-50"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Wykonaj ({preview.count ?? count})
                  </button>
                ) : (
                  <span className="text-xs text-success flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Brak operacji do wykonania
                  </span>
                )}
                <button
                  onClick={() => { setState('idle'); setPreview(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-secondary hover:text-text-secondary transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                  Zamknij
                </button>
              </div>
            </div>
          )}

          {/* Confirmation dialog for destructive operations */}
          {showConfirm && (
            <div className="mt-3 p-3 rounded-lg bg-danger/10 border border-danger/30 space-y-2">
              <p className="text-xs text-danger">
                Ta operacja jest nieodwracalna. Wpisz <strong>POTWIERDŹ</strong> aby kontynuować.
              </p>
              {destructive && title.toLowerCase().includes('renumer') && (
                <p className="text-[11px] text-danger/80">
                  Ta operacja zmieni numery faktur. Klienci, którzy pobrali PDF, będą mieli stare numery.
                </p>
              )}
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Wpisz POTWIERDŹ"
                className="w-full bg-surface-raised border border-danger/20 rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:border-danger/50"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleExecute}
                  disabled={confirmText !== 'POTWIERDŹ'}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger/20 border border-danger/30 text-xs text-danger hover:bg-danger/30 transition-all disabled:opacity-30"
                >
                  <Play className="w-3.5 h-3.5" />
                  Potwierdzam
                </button>
                <button
                  onClick={() => { setShowConfirm(false); setConfirmText(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-secondary hover:text-text-secondary transition-all"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}

          {/* Done */}
          {state === 'done' && result && (
            <div className="mt-3 flex items-center gap-2 text-xs text-success">
              <CheckCircle className="w-3.5 h-3.5" />
              {result.message} {result.successCount !== undefined && `(${result.successCount} sukces, ${result.failCount || 0} błędów)`}
            </div>
          )}

          {/* Error */}
          {state === 'error' && error && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-danger">
                <AlertTriangle className="w-3.5 h-3.5" />
                {error}
              </div>
              <button
                onClick={() => { setState('idle'); setError(null); }}
                className="text-xs text-text-secondary hover:text-text-secondary underline"
              >
                Spróbuj ponownie
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
